import type { PhoneLine } from '../types'

/**
 * The tooltip's minimum width, in px. Exported so the positioning code that clamps
 * it inside its container reserves the same width the panel actually occupies —
 * previously a separate literal that had drifted 20px away from this one.
 *
 * Applied via inline `style` rather than a Tailwind class: `min-w-[190px]` would be
 * a third copy of the number, and Tailwind's JIT can't read a JS constant (it scans
 * for static class strings), so the class and this value could drift apart again.
 */
export const TOOLTIP_MIN_WIDTH = 190

/**
 * Single lightweight floating panel, positioned by the page from the shared
 * hovered line (not a per-element Radix tooltip — impractical at ~1150 marks
 * and can't sync a hover across the grid and map). Rendered inside the page's
 * `relative` wrapper.
 */
export function PhoneLineTooltip({
  line,
  city,
  pos,
}: {
  line: PhoneLine | null
  city: string | null
  pos: { top: number; left: number } | null
}) {
  if (!line || !pos) return null

  return (
    <div
      role="tooltip"
      className="pointer-events-none absolute z-10 rounded-md border bg-popover px-3 py-2.5 text-xs text-muted-foreground shadow-md"
      style={{
        top: pos.top,
        left: pos.left,
        minWidth: TOOLTIP_MIN_WIDTH,
      }}>
      {line.status === 'idle' ? (
        <>
          <div className="mb-1 font-medium text-foreground">
            Line {line.line_number}
          </div>
          <div>Idle, waiting for a call</div>
        </>
      ) : (
        <>
          <div className="mb-1.5 flex items-center gap-1.5">
            <span className="inline-block size-2 rounded-[2px] bg-fuchsia-500 dark:bg-fuchsia-400" />
            <span className="font-medium text-foreground">
              Line {line.line_number}
            </span>
          </div>
          <TooltipRow label="Duty" value={line.duty_name ?? 'Unassigned'} />
          <TooltipRow label="Patient" value={line.patient_name ?? 'Unknown'} />
          <TooltipRow label="Phone" value={line.phone_number ?? '—'} />
          {city && <TooltipRow label="City" value={`${city} (approx.)`} />}
        </>
      )}
    </div>
  )
}

function TooltipRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="mb-0.5 last:mb-0">
      <span className="text-muted-foreground/70">{label} </span>
      <span className="text-foreground">{value}</span>
    </div>
  )
}
